import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Chip, Table, TableHead, TableBody, TableRow,
  TableCell, IconButton, CircularProgress, Alert, Tooltip, Divider, Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  getTeam, getSprints, getVelocity, deleteSprint, getOutcomesList, getApplicationsList,
} from './services/capacityApi';
import VelocityChart from './VelocityChart';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const BASIS_LABELS = { historical: 'Historical', blended: 'Blended (75/25)', rolling_3: 'Rolling 3' };
const BASIS_COLORS = { historical: 'default', blended: 'warning', rolling_3: 'success' };

function statFmt(n) { return n == null ? '—' : typeof n === 'number' ? n.toFixed(2) : n; }

export default function TeamDashboard({ teamId, onBack, onEditTeam, onAddSprint, onEditSprint, isReadOnly }) {
  const [team, setTeam] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [allocation, setAllocation] = useState(null); // summed allocation %
  const [outcomeMap, setOutcomeMap] = useState({});
  const [applicationMap, setApplicationMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, sp, vel, oRes, aRes] = await Promise.all([
        getTeam(teamId),
        getSprints(teamId),
        getVelocity(teamId),
        getOutcomesList(),
        getApplicationsList(),
      ]);
      setTeam(t);
      setSprints((sp?.sprints || []).sort((a, b) => b.start_date.localeCompare(a.start_date)));
      setVelocity(vel);

      const oMap = {};
      for (const o of (oRes?.outcomes || [])) oMap[o.id] = o.name;
      setOutcomeMap(oMap);

      const aMap = {};
      for (const a of (aRes?.applications || [])) aMap[a.id] = a.name;
      setApplicationMap(aMap);

      // Fetch allocation summary lazily
      fetchAllocation(t);
    } catch (e) {
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  async function fetchAllocation(t) {
    if (!t) return;
    const year = new Date().getFullYear();
    const memberIds = new Set((t.members || []).filter((m) => !m.archived).map((m) => m.roster_member_id));
    const outcomeIds = new Set(t.outcome_ids || []);
    if (memberIds.size === 0 || outcomeIds.size === 0) { setAllocation(0); return; }
    try {
      const res = await fetch(`${API_BASE}/api/roster/outcome-allocations/${year}`);
      if (!res.ok) return;
      const data = await res.json();
      const allocations = data?.outcome_allocations || [];
      let total = 0;
      for (const oa of allocations) {
        if (!outcomeIds.has(oa.outcome_id)) continue;
        for (const role of oa.staffed_roles || []) {
          if (memberIds.has(role.roster_member_id)) {
            const monthlyAvg = (role.monthly_allocations || []).reduce((s, v) => s + v, 0) / 12;
            total += monthlyAvg;
          }
        }
      }
      setAllocation(Math.round(total));
    } catch {
      // Non-critical — leave as null
    }
  }

  const { completedSprints, avgSpPerDD, activeMembers } = useMemo(() => {
    const completed = sprints.filter((s) => s.status === 'completed' && s.actual_sp != null);
    const avg = completed.length > 0
      ? completed.reduce((sum, s) => sum + (s.actual_sp / (s.total_planned_devdays || 1)), 0) / completed.length
      : null;
    const active = (team?.members || []).filter((m) => !m.archived);
    return { completedSprints: completed, avgSpPerDD: avg, activeMembers: active };
  }, [sprints, team]);

  async function handleDeleteSprint(sprintId) {
    if (!window.confirm('Delete this sprint? This cannot be undone.')) return;
    setDeleting(sprintId);
    try {
      await deleteSprint(teamId, sprintId);
      setSprints((prev) => prev.filter((s) => s.id !== sprintId));
    } catch {
      setError('Failed to delete sprint');
    } finally {
      setDeleting(null);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!team) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} size="small">Teams</Button>
        <Typography variant="h6" sx={{ flex: 1 }}>{team.name}</Typography>
        {!isReadOnly && (
          <>
            <Button startIcon={<EditIcon />} size="small" variant="outlined" onClick={() => onEditTeam(teamId)}>
              Edit Team
            </Button>
            <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => onAddSprint(teamId)}>
              Add Sprint
            </Button>
          </>
        )}
      </Box>

      {team.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{team.description}</Typography>
      )}

      {/* Linked outcomes */}
      {(team.outcome_ids || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>Outcomes:</Typography>
          {(team.outcome_ids || []).slice(0, 5).map((id) => (
            <Chip key={id} label={outcomeMap[id] || id.substring(0, 8) + '…'} size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
          ))}
          {(team.outcome_ids || []).length > 5 && (
            <Chip label={`+${team.outcome_ids.length - 5}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
          )}
        </Box>
      )}

      {/* Linked applications */}
      {(team.application_ids || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>Applications:</Typography>
          {(team.application_ids || []).slice(0, 5).map((id) => (
            <Chip key={id} label={applicationMap[id] || id.substring(0, 8) + '…'} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.6rem', height: 18 }} />
          ))}
          {(team.application_ids || []).length > 5 && (
            <Chip label={`+${team.application_ids.length - 5}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
          )}
        </Box>
      )}

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Members</Typography>
          <Typography variant="h6">{activeMembers.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Sprints</Typography>
          <Typography variant="h6">{sprints.length}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 140 }}>
          <Typography variant="caption" color="text.secondary">Avg SP / Dev Day</Typography>
          <Typography variant="h6">{avgSpPerDD != null ? avgSpPerDD.toFixed(2) : '—'}</Typography>
        </Paper>
        {velocity?.dev && (
          <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">Dev Velocity</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography variant="h6">{velocity.dev.rate.toFixed(2)}</Typography>
              <Chip
                label={BASIS_LABELS[velocity.dev.basis] || velocity.dev.basis}
                size="small"
                color={BASIS_COLORS[velocity.dev.basis] || 'default'}
                variant="outlined"
                sx={{ fontSize: '0.55rem', height: 16 }}
              />
            </Box>
          </Paper>
        )}
        {velocity?.qa && (
          <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">QA Velocity</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography variant="h6">{velocity.qa.rate.toFixed(2)}</Typography>
              <Chip
                label={BASIS_LABELS[velocity.qa.basis] || velocity.qa.basis}
                size="small"
                color={BASIS_COLORS[velocity.qa.basis] || 'default'}
                variant="outlined"
                sx={{ fontSize: '0.55rem', height: 16 }}
              />
            </Box>
          </Paper>
        )}
        {allocation != null && (
          <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">Allocation vs Outcomes</Typography>
            <Typography variant="h6">{allocation}%</Typography>
            <Typography variant="caption" color="text.secondary">avg across linked outcomes</Typography>
          </Paper>
        )}
      </Box>

      {/* Velocity chart */}
      {completedSprints.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <VelocityChart sprints={sprints} />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Sprints table */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Sprints</Typography>
      {sprints.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No sprints yet. Click "Add Sprint" to plan your first sprint.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell align="center">Dev Days</TableCell>
                <TableCell align="center">Exp Dev SP</TableCell>
                <TableCell align="center">Exp QA SP</TableCell>
                <TableCell align="center">Act Dev SP</TableCell>
                <TableCell align="center">Act QA SP</TableCell>
                <TableCell align="center">Status</TableCell>
                {!isReadOnly && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sprints.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {s.start_date} → {s.end_date}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{s.total_planned_devdays}</TableCell>
                  <TableCell align="center">{s.expected_sp_dev != null ? statFmt(s.expected_sp_dev) : statFmt(s.expected_sp)}</TableCell>
                  <TableCell align="center">{s.expected_sp_qa != null ? statFmt(s.expected_sp_qa) : '—'}</TableCell>
                  <TableCell align="center">{s.actual_sp_dev != null ? statFmt(s.actual_sp_dev) : '—'}</TableCell>
                  <TableCell align="center">{s.actual_sp_qa != null ? statFmt(s.actual_sp_qa) : '—'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={s.status === 'completed' ? 'Completed' : 'Planned'}
                      size="small"
                      color={s.status === 'completed' ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell align="center">
                      <Tooltip title="Edit sprint">
                        <IconButton size="small" onClick={() => onEditSprint(teamId, s.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete sprint">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting === s.id}
                            onClick={() => handleDeleteSprint(s.id)}
                          >
                            {deleting === s.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
